import { APPLE_STANDARD_EULA } from '@/membersheep/config';
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/shared/site';
import type { MsCopy } from './en';

/** MemberSheep's Traditional Chinese (Taiwan) copy. The legal pages are translations; English governs. */
export const zhTW: MsCopy = {
  htmlLang: 'zh-Hant-TW',
  ogLocale: 'zh_TW',
  languageLabel: '繁中',

  nav: {
    home: 'MemberSheep 首頁',
    primary: 'MemberSheep',
    privacy: '隱私權',
    terms: '條款',
    support: '支援',
    byLabs: 'Travo Labs 出品',
    language: '語言'
  },

  seo: {
    ogImage: '/og-image-membersheep-zh-tw.png',
    ogImageAlt: 'MemberSheep：會員卡，不用再翻錢包。iPhone 會員卡收納 App。',
    home: {
      title: 'MemberSheep：iPhone 會員卡收納 App',
      description: '把所有會員卡、集點卡收進 iPhone。條碼掃一次，結帳時一點就出示。不用註冊，資料留在你的裝置上。'
    },
    privacy: {
      title: '隱私權政策 | MemberSheep',
      description: 'MemberSheep 在你的 iPhone 上保存哪些資料、哪些資料會因廣告與購買而傳出，以及你能做的選擇。'
    },
    terms: {
      title: '使用條款 | MemberSheep',
      description: 'MemberSheep 與 MemberSheep Plus 的使用條款，以 Apple 標準 EULA 為基礎。'
    },
    support: {
      title: '支援 | MemberSheep',
      description: '關於隱藏的卡片、恢復 Plus、iCloud 同步與廣告隱私的解答，以及聯絡方式。'
    },
    notFound: '找不到頁面 | MemberSheep'
  },

  home: {
    iconAlt: 'MemberSheep App 圖示',
    title: '會員卡，\n不用再翻錢包。',
    body: '會員卡掃一次，結帳時一點就出示。不用註冊，不經過我們的伺服器。',
    comingSoon: '即將登上 App Store',
    download: '在 App Store 下載',
    heroCards: {
      cafe: { name: '街角咖啡', kind: '集點卡' },
      gym: { name: '北區健身房', kind: '會員卡' },
      grocer: { name: '青青蔬果', kind: '紅利卡' },
      books: { name: '書頁書店', kind: '讀書會' }
    },
    features: [
      {
        title: '掃描、匯入或手動輸入',
        body: '用相機對準條碼、選一張卡片照片，或直接輸入號碼。'
      },
      {
        title: '結帳時自動調亮',
        body: '打開卡片就調到最亮，掃描機一次就讀得到。'
      },
      {
        title: '資料留在你的 iPhone',
        body: '不用註冊。想同步，就用你自己的 iCloud。'
      }
    ],
    plusLead: 'MemberSheep',
    plusAccent: 'Plus',
    plusBody: '免費版可存 5 張卡、2 個分類，並顯示廣告。Plus 解除限制、移除廣告，可選月繳、年繳或一次買斷。',
    plusItems: ['卡片與分類無上限', '完全沒有廣告', '任一張卡都能放上主畫面小工具'],
    links: {
      privacy: '哪些資料會離開裝置、為什麼',
      terms: '訂閱、商標與其他細則',
      support: `常見問題與 ${SUPPORT_EMAIL}`
    }
  },

  quickAnswers: {
    title: '快速解答',
    items: [
      {
        question: 'MemberSheep 免費嗎？',
        answer: '是的。免費版最多可存 5 張卡、2 個分類，並顯示廣告。MemberSheep Plus 解除限制並移除廣告，可選月繳、年繳訂閱或一次買斷。'
      },
      {
        question: '需要註冊帳號嗎？',
        answer: '不需要。沒有註冊流程，卡片存在你的 iPhone 上；想同步的話，可以用你自己的 iCloud。'
      },
      {
        question: '支援哪些條碼？',
        answer:
          'QR code、Aztec、Data Matrix、PDF417、Code 128、Code 39、Code 93、Codabar、EAN-13、EAN-8、UPC-A、UPC-E、ITF 與 ITF-14。可以用掃描、匯入照片或手動輸入的方式新增卡片。'
      },
      {
        question: '支援哪些 iPhone？',
        answer: '搭載 iOS 17 或更新版本的 iPhone 都能使用。App 提供英文與繁體中文介面。'
      },
      {
        question: 'MemberSheep 是誰做的？',
        answer: '台灣的獨立工作室 Travo Labs，也是旅行 App Travo 的開發者。'
      }
    ]
  },

  schema: {
    description:
      'MemberSheep 是 iPhone 上的會員卡、集點卡收納 App。條碼或 QR code 掃一次，結帳時一點就以最高亮度出示。不需帳號，卡片留在裝置上，也可用你自己的 iCloud 同步。免費版可存 5 張卡、2 個分類並顯示廣告；MemberSheep Plus（月繳、年繳或一次買斷）解除限制並移除廣告。',
    featureList: [
      '以掃描、匯入照片或手動輸入新增卡片',
      '支援 14 種條碼與 QR code 格式，包括 EAN-13、Code 128、QR 與 PDF417',
      '打開卡片自動調到最亮',
      '分類、搜尋，並依最近使用排序',
      '任選一張卡放上主畫面小工具',
      '可選擇透過你的私人 iCloud 同步',
      '不需帳號，卡片留在你的 iPhone',
      '英文與繁體中文介面'
    ]
  },

  legal: {
    governs: '本頁為英文版的翻譯，如有歧異，以英文版為準。',
    onThisPage: '本頁內容',
    otherLanguage: 'English version'
  },

  privacy: {
    title: '隱私權政策',
    updated: '最後更新：2026 年 10 月 25 日',
    summaryTitle: '重點摘要',
    summary: [
      '你的卡片不會傳到我們這裡。它們存在你的 iPhone 上；開啟同步時，也存在你自己的 iCloud。',
      '免費版會顯示 Google AdMob 廣告，在你同意的範圍內使用裝置與廣告資料。',
      '購買透過 Apple 進行。RevenueCat 會保存一組匿名 ID，讓 App 知道你有 Plus。'
    ],
    sections: [
      {
        id: 'who',
        heading: '我們是誰',
        paragraphs: [
          `MemberSheep 由 ${LEGAL_NAME} 開發，以 Travo Labs 名義營運的台灣獨立開發者（下稱「我們」）。我們對本 App 與本政策負責。聯絡信箱：${SUPPORT_EMAIL}。`,
          'MemberSheep 沒有帳號系統，也沒有任何會接收你卡片資料的自有伺服器。本政策說明 App 保存哪些資料、它所使用的服務會收到什麼，以及你可以調整的設定。'
        ]
      },
      {
        id: 'device',
        heading: '存在你 iPhone 上的資料',
        paragraphs: [
          '你新增的卡片——名稱、條碼或 QR code 內容與格式、顏色、標誌、分類、備註——以及每張卡最後使用的時間，存在 iPhone 上這個 App 的儲存空間；只有在你開啟 iCloud 同步時（見下方），才會另存一份在你自己的 iCloud。主畫面小工具讀取的也是同一份資料。',
          '就像其他 App 一樣，你 iPhone 本身的備份（iCloud 備份或電腦備份）會包含這些資料。我們不會收到。'
        ]
      },
      {
        id: 'camera',
        heading: '相機與照片',
        paragraphs: [
          '只有在你掃描卡片時才會使用相機。畫面在 iPhone 上辨識條碼，不會被儲存或傳送到任何地方。',
          '從照片匯入卡片或挑選標誌時，你選的圖片只在 iPhone 上讀取，只有你保留的裁切後標誌會跟著卡片儲存。'
        ]
      },
      {
        id: 'icloud',
        heading: 'iCloud 同步（選用）',
        paragraphs: [
          'iCloud 同步預設關閉，需要你在 App 的設定中開啟。開啟後，卡片、分類與標誌會存在你 Apple ID 底下的私人 iCloud 資料庫，讓你的裝置保持一致。資料由 Apple 保存，我們無法讀取你的私人資料庫。',
          '你隨時可以關閉同步。若要刪除 iCloud 中的資料，請在 iOS 設定的 iCloud 儲存空間中刪除 MemberSheep 的資料。Apple 如何處理 iCloud 資料，請見 Apple 的隱私權政策。'
        ],
        links: [{ label: 'Apple 隱私權政策', href: 'https://www.apple.com/tw/legal/privacy/' }]
      },
      {
        id: 'ads',
        heading: '廣告（免費版）',
        paragraphs: [
          '免費版會顯示 Google AdMob 廣告。為了顯示與衡量廣告、防範詐欺，Google 可能會收到你的 IP 位址、裝置與 App 資訊、廣告互動，以及——只有在你允許追蹤時——裝置的廣告識別碼（IDFA）。',
          '任何廣告載入前，App 會依你所在地的法律要求取得同意：在歐洲經濟區、英國與瑞士會顯示 Google 的同意表單，並會顯示 Apple 的「App 追蹤透明度」提示。之後可以在 App 設定的「廣告隱私選項」（有提供時）更改廣告選擇，並在 iOS「設定」→「隱私權與安全性」→「追蹤」更改追蹤權限。若你不同意，Google 仍可能以較少的資料顯示廣告：在上述地區會改為非個人化或有限度的廣告；不允許追蹤時，也不會分享廣告識別碼。',
          '使用 MemberSheep Plus 時沒有廣告，也不會啟動廣告 SDK。'
        ],
        links: [{ label: 'Google 如何使用採用其服務之 App 的資訊', href: 'https://policies.google.com/technologies/partner-sites?hl=zh-TW' }]
      },
      {
        id: 'purchases',
        heading: '購買',
        paragraphs: [
          'MemberSheep Plus 透過 Apple 購買，付款由 Apple 處理，我們不會看到你的付款資訊。',
          'App 使用 RevenueCat 確認 Plus 是否有效。RevenueCat 會收到 App 產生的匿名 ID（不是你的姓名或 Apple ID）、購買紀錄與收據，以及基本裝置資訊，例如機型、iOS 版本、App Store 國家／地區與 IP 位址。你的卡片永遠不會被傳送。'
        ],
        links: [{ label: 'RevenueCat 隱私權政策', href: 'https://www.revenuecat.com/privacy' }]
      },
      {
        id: 'not',
        heading: '我們不做的事',
        paragraphs: [],
        items: [
          'App 內沒有帳號、註冊或收集電子郵件。',
          '沒有我們自己的分析或追蹤 SDK。',
          '我們不會為了金錢出售或分享你的個人資料。'
        ]
      },
      {
        id: 'children',
        heading: '兒童',
        paragraphs: [
          `MemberSheep 並非以 13 歲以下兒童為對象，我們也不會在知情的情況下收集兒童的個人資料。如果你認為有兒童提供了個人資料給我們，請寫信至 ${SUPPORT_EMAIL}，我們會將其刪除。`
        ]
      },
      {
        id: 'retention',
        heading: '資料的保存與刪除',
        paragraphs: [
          '卡片會留在你的 iPhone 上，直到你刪除卡片或 App；在 iCloud 中則保留到你在那裡刪除為止。Google 與 RevenueCat 依其各自政策保存資料。',
          `若要刪除 RevenueCat 為你的 App 保存的購買紀錄，請寫信至 ${SUPPORT_EMAIL}，我們會請 RevenueCat 刪除。Apple 會另外保存你的購買紀錄。`
        ]
      },
      {
        id: 'rights',
        heading: '你的權利',
        paragraphs: [
          '依你所在地的法律——例如歐盟與英國 GDPR、台灣《個人資料保護法》或美國加州法律——你可能有權查詢、更正或刪除你的個人資料，反對或限制其使用，以及撤回同意。由於你的卡片從不會傳到我們這裡，大部分事項都能透過上述設定自行處理。其他需求請來信，我們會在 30 天內回覆。',
          '你也可以向所在地的個人資料保護主管機關申訴。'
        ]
      },
      {
        id: 'transfers',
        heading: '跨境傳輸',
        paragraphs: ['Google 與 RevenueCat 可能在美國及其他國家處理資料，並依其政策所述的保護措施進行。']
      },
      {
        id: 'website',
        heading: '本網站',
        paragraphs: [
          'www.travolabs.com 由 Cloudflare 託管，Cloudflare 會處理 IP 位址以傳送並保護本網站。造訪次數以不使用 Cookie 的 Cloudflare Web Analytics 統計。網站字型由 Google Fonts 提供，因此載入頁面時 Google 會收到你的 IP 位址。',
          '我們自己不設定任何 Cookie。若你在 Travo 頁面播放示範影片，影片會從 YouTube 載入，YouTube 可能依 Google 的政策設定 Cookie。'
        ],
        links: [
          { label: 'Cloudflare 隱私權政策', href: 'https://www.cloudflare.com/privacypolicy/' },
          { label: 'Google 隱私權政策', href: 'https://policies.google.com/privacy?hl=zh-TW' }
        ]
      },
      {
        id: 'changes',
        heading: '政策變更',
        paragraphs: [
          '若本政策有所變更，我們會更新上方日期。若變更影響 App 收集的資料，我們會在生效前於 App 或本頁說明。'
        ]
      },
      {
        id: 'contact',
        heading: '聯絡我們',
        paragraphs: [`隱私權相關問題：${SUPPORT_EMAIL}。`]
      }
    ]
  },

  terms: {
    title: '使用條款',
    updated: '最後更新：2026 年 10 月 25 日',
    sections: [
      {
        id: 'agreement',
        heading: '協議',
        paragraphs: [
          `MemberSheep 依 Apple 的「授權應用程式終端使用者授權協議」（下稱「標準 EULA」）授權給你。本條款是你與以 Travo Labs 名義營運的 ${LEGAL_NAME}（下稱「我們」）之間，針對 MemberSheep 的補充約定。使用本 App 即表示你同意兩者。兩者衝突時，以標準 EULA 為準。`
        ],
        links: [{ label: 'Apple 標準 EULA', href: APPLE_STANDARD_EULA }]
      },
      {
        id: 'plus',
        heading: 'MemberSheep Plus',
        paragraphs: ['Plus 提供月繳或年繳的自動續訂方案，以及一次買斷的終身方案。價格會在購買前顯示於 App 中。'],
        items: [
          '確認購買時，費用會向你的 Apple ID 收取。',
          '訂閱會自動續訂，除非你在當期結束前至少 24 小時取消。續訂費用會在當期結束前 24 小時內收取。',
          '可在 Apple ID 的訂閱設定，或 App 內的「管理 Plus」管理或取消訂閱。',
          '若有提供免費試用，購買訂閱時，未使用完的試用期即告結束。',
          '退款由 Apple 依其政策處理，你可以從 App 內的「管理 Plus」提出申請。',
          '在登入同一 Apple ID 的其他裝置上，使用「恢復購買」即可恢復 Plus。'
        ]
      },
      {
        id: 'free',
        heading: '免費版與隱藏的卡片',
        paragraphs: [
          '沒有 Plus 時，App 最多顯示 5 張卡與 2 個分類，並顯示廣告。Plus 結束後，超出限制的卡片與分類會被隱藏而非刪除，重新訂閱或恢復購買後就會回來。'
        ]
      },
      {
        id: 'cards',
        heading: '你的卡片',
        paragraphs: [
          '你需為自己新增的卡片負責，請只新增你有權使用的卡片。',
          '店家是否接受手機上出示的條碼由店家決定。我們無法保證任何店家會掃描或承認某張卡，也不對你錯過的優惠、點數或折扣負責。',
          '你的卡片存在你的裝置上，以及你選擇開啟同步時的 iCloud 中。請自行備份，我們無法替你救回卡片。'
        ]
      },
      {
        id: 'trademarks',
        heading: '品牌名稱與標誌',
        paragraphs: [
          '預設卡片中各商店與品牌的名稱、標誌與商標皆屬其所有人，僅用於協助你辨認自己的卡片。MemberSheep 與這些品牌沒有任何關聯，也未獲其背書或贊助。',
          'MemberSheep、Travo Labs 及其標誌屬於我們。'
        ]
      },
      {
        id: 'ads',
        heading: '廣告',
        paragraphs: ['免費版會顯示第三方廣告。我們無法控制、也不對廣告主或其廣告連往的網站與 App 負責。']
      },
      {
        id: 'liability',
        heading: '免責與責任限制',
        paragraphs: [
          '本 App 依標準 EULA 所述「按現狀」提供。在法律允許的範圍內，我們不對間接或衍生損失負責，也不對卡片無法掃描或不被接受所造成的損失負責。本條款不限制你依消費者保護法享有、且不得拋棄的權利。'
        ]
      },
      {
        id: 'changes',
        heading: '變更',
        paragraphs: ['我們可能更新本 App 與本條款。條款變更時我們會更新上方日期；變更後繼續使用本 App，即表示你接受變更。']
      },
      {
        id: 'law',
        heading: '準據法',
        paragraphs: [
          '本條款以中華民國（台灣）法律為準據法，並以臺灣臺北地方法院為第一審管轄法院；但你居住國的消費者保護法賦予你在當地提起訴訟之權利者，不在此限。'
        ]
      },
      {
        id: 'contact',
        heading: '聯絡我們',
        paragraphs: [`條款相關問題：${SUPPORT_EMAIL}。`]
      }
    ]
  },

  support: {
    title: '支援',
    faqTitle: '常見問題',
    faq: [
      {
        question: 'Plus 結束後，有些卡片不見了。',
        answer: '它們只是被隱藏，沒有被刪除。免費版會顯示你最早新增的 5 張卡與前 2 個分類。重新訂閱或恢復購買後，所有卡片與分類都會回來。'
      },
      {
        question: '換了新 iPhone，要怎麼恢復 Plus？',
        answer: '登入同一個 Apple ID，點首頁的 Plus 按鈕，再點「恢復購買」。'
      },
      {
        question: '要怎麼取消訂閱或申請退款？',
        answer: '從 Plus 按鈕打開「管理 Plus」，選擇「管理訂閱」或「申請退款」。也可以在 Apple ID 的訂閱設定中取消。退款由 Apple 決定。'
      },
      {
        question: 'iCloud 同步怎麼運作？',
        answer:
          '在 App 的設定中開啟「iCloud 同步」。卡片會存到你的私人 iCloud 資料庫，並出現在登入同一 Apple ID 的其他裝置上，我們看不到它們。關閉同步或登出 iCloud，卡片仍會留在你的 iPhone 上。'
      },
      {
        question: '要怎麼把卡片搬到新 iPhone？',
        answer: '換機前開啟 iCloud 同步，或用舊 iPhone 的備份還原新 iPhone。'
      },
      {
        question: '店家的掃描機讀不到我的卡。',
        answer: '請在 App 中打開卡片，而不是用小工具出示：App 會把螢幕調到最亮。部分條碼類型（MaxiCode、GS1 DataBar、Micro QR）目前還無法顯示。你可以用「輸入代碼」加入卡號，並選一個店家掃描機讀得到的格式（預設為 Code 128）；但有些掃描機只接受卡片原本的條碼類型。'
      },
      {
        question: '要怎麼更改廣告隱私選項？',
        answer: '在 App 設定中點「廣告隱私選項」（依法律要求的地區才會顯示）。追蹤權限在 iOS「設定」→「隱私權與安全性」→「追蹤」。'
      },
      {
        question: '要怎麼刪除我所有的資料？',
        answer: '刪除 App 即可移除 iPhone 上的所有資料。若用過 iCloud 同步，也請在 iOS 設定的 iCloud 儲存空間中刪除 MemberSheep 的資料。'
      }
    ],
    mailTitle: '還是需要協助？',
    mailBody: '寫信給我們，我們會在兩個工作天內回覆。'
  },

  notFound: {
    heading: '這張卡不在錢包裡。',
    body: '你要找的頁面可能已經移動了。',
    cta: '回到 MemberSheep'
  },

  footer: {
    rights: 'Travo Labs'
  }
};
